import React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { QCFinding } from "../../types/qcTypes";
import { rehypeQcFindings } from "./qcRehypePlugin";
import { qcSanitizeSchema } from "./qcRedlineShared";

// Markdown content renderer for QC findings. Parses `sectionContent` as
// markdown, sanitizes, then rehypeQcFindings overlays the redline spans using
// each finding's source offsets (mdast positions survive into hast). Used by
// independent QC and by the panel renderer's markdown branch.

interface MarkdownRedlineContentProps {
  findings: QCFinding[];
  sectionContent: string;
  components: Components;
  className?: string;
}

const MarkdownRedlineContent: React.FC<MarkdownRedlineContentProps> = ({
  findings,
  sectionContent,
  components,
  className,
}) => {
  const rehypePlugins = React.useMemo(
    () => [
      [rehypeSanitize, qcSanitizeSchema] as [
        typeof rehypeSanitize,
        typeof qcSanitizeSchema,
      ],
      [rehypeQcFindings, findings] as [typeof rehypeQcFindings, QCFinding[]],
    ],
    [findings],
  );

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // react-markdown types don't model parameterized plugins well; the
        // tuple form is fully supported at runtime.
        rehypePlugins={rehypePlugins as never}
        components={components}
      >
        {sectionContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRedlineContent;
